import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EGroupActions, trackGroupActionClick } from './tracking';
import {
  archiveChats,
  archiveAllChats,
  markChatsAsRead,
  markAllChatsAsRead,
  restoreChatsFromArchive,
  restoreAllChatsFromArchive,
} from '../../actions/actionsChatsList';
import { openChatActionModal } from '../../actions/chatActionModal';
import { chatListSelector } from '../../selectors';
import { ERequestApiStatus } from '../../types/appState';
import { EChatAction } from '../../types/chatActionModal';
import { ERoute } from '../../types/route';

type TRoutesAvailableToChangeForRead = Exclude<ERoute, ERoute.Spam | ERoute.Starred>;
type TRoutesAvailableToChangeForArchive = Exclude<ERoute, ERoute.Spam | ERoute.Archive | ERoute.Starred>;

const RouteToReadSectionMap = {
  messages: 'main',
  archive: 'archive',
  unread: 'unread',
} as const;

const RouteToArchiveSectionMap = {
  messages: 'main',
  unread: 'unread',
} as const;

export const useListActionButtons = () => {
  const dispatch = useDispatch();

  const { isChangeListActive, selectedChats, currentRoute, chats, status } = useSelector(chatListSelector);

  const isReadButtonDisabled = React.useMemo(() => {
    const unreadChats = chats.filter(({ lastMessage }) => lastMessage?.status !== 'readByRecipient');

    // если нет непрочитанных чатов, то читать нечего
    if (!unreadChats.length) {
      return true;
    }

    // если выбраны чаты и среди них нет непрочитанных, то тоже читать нечего
    return selectedChats.length > 0 && !unreadChats.some(({ chatId }) => selectedChats.includes(chatId));
  }, [selectedChats, chats]);

  const onArchiveClick = React.useCallback(() => {
    if (selectedChats.length) {
      dispatch(archiveChats(selectedChats));
      trackGroupActionClick(
        EGroupActions.Archive,
        chats.filter(({ chatId }) => selectedChats.includes(chatId)),
      );

      return;
    }

    dispatch(archiveAllChats(RouteToArchiveSectionMap[currentRoute as TRoutesAvailableToChangeForArchive]));
    trackGroupActionClick(EGroupActions.Archive);
  }, [dispatch, selectedChats, currentRoute, chats]);

  const onDeleteClick = React.useCallback(
    () =>
      dispatch(
        openChatActionModal({
          buttonText: 'Удалить',
          text: `Точно хотите удалить чат${selectedChats.length === 1 ? '' : 'ы'}?`,
          chatAction: EChatAction.GroupDelete,
        }),
      ),
    [dispatch, selectedChats],
  );

  const onReadClick = React.useCallback(() => {
    if (selectedChats.length) {
      dispatch(markChatsAsRead(selectedChats));
      trackGroupActionClick(
        EGroupActions.Read,
        chats.filter(({ chatId }) => selectedChats.includes(chatId)),
      );

      return;
    }

    dispatch(markAllChatsAsRead(RouteToReadSectionMap[currentRoute as TRoutesAvailableToChangeForRead]));
    trackGroupActionClick(EGroupActions.Read);
  }, [dispatch, currentRoute, selectedChats, chats]);

  const onRestoreClick = React.useCallback(() => {
    if (selectedChats.length) {
      dispatch(restoreChatsFromArchive(selectedChats));
      trackGroupActionClick(
        EGroupActions.Unarchive,
        chats.filter(({ chatId }) => selectedChats.includes(chatId)),
      );

      return;
    }

    dispatch(restoreAllChatsFromArchive());
    trackGroupActionClick(EGroupActions.Unarchive);
  }, [dispatch, selectedChats, chats]);

  const isMultiple = React.useMemo(
    () => !selectedChats.length && currentRoute !== ERoute.Starred,
    [selectedChats, currentRoute],
  );

  return React.useMemo(
    () => ({
      isChangeListActive,
      isReadButtonDisabled,
      isArchiveButtonDisabled: !isMultiple && !selectedChats.length,
      isDeleteButtonDisabled: !isMultiple && !selectedChats.length,
      onArchiveClick,
      onDeleteClick,
      onReadClick,
      onRestoreClick,
      isMultiple,
      isPending: status === ERequestApiStatus.Pending,
      isArchive: currentRoute === ERoute.Archive,
    }),
    [
      isChangeListActive,
      isReadButtonDisabled,
      isMultiple,
      selectedChats.length,
      onArchiveClick,
      onDeleteClick,
      onReadClick,
      onRestoreClick,
      status,
      currentRoute,
    ],
  );
};
